import { SMARTY_API_SERVICE_NAME } from '../../configs/constants'
import { ISearchOptions } from '../../types/ride-types'
import ApiService from '../api-service'
import { postLogData } from '../appApi'

export const getPlaces = async (input: string) => {
  const autocompleteService = new google.maps.places.AutocompleteService()
  return await autocompleteService.getPlacePredictions({
    input,
    componentRestrictions: {
      country: 'us'
    },
    region: 'us',
    language: 'en', // Force English language results
    locationBias: new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(40.477399, -74.25909),
      new window.google.maps.LatLng(40.917577, -73.700272)
    ),
    types: ['address']
  })
}
function buildAddress(suggestion: GetSmartAddressData) {
  let whiteSpace = ''
  if (suggestion.secondary) {
    if (suggestion.entries > 1) {
      suggestion.secondary += ' (' + suggestion.entries + ' entries)'
    }
    whiteSpace = ' '
  }
  return suggestion.street_line + whiteSpace + suggestion.secondary + ', ' + suggestion.city + ', ' + suggestion.state + ' ' + suggestion.zipcode
}

type GetSmartAddressData = {
  city: string;
  entries: number;
  secondary: string;
  state: string;
  street_line: string;
  zipcode: string;
}

const logErrorOfApi = (response: unknown, type: string) => {
  postLogData({
    type,
    message: response
  })
}

// Helper function to check if text contains only English/Latin characters
const isEnglishText = (text: string): boolean => {
  // Allow Latin characters, numbers, spaces, and common punctuation used in addresses
  const englishPattern = /^[a-zA-Z0-9\s\-.,#()&']+$/
  return englishPattern.test(text)
}

type GetSmartAddressByStreetData = {
  search: string;
  selected: string;
  language?: string;
}
const getSmartyAddressByStreet = async (data: GetSmartAddressByStreetData) => ApiService.get<GetSmartAddressByStreetData, Promise<{ suggestions: GetSmartAddressData[] }>>('/smarty-autocomplete/lookup', {
  data
})

export const getSmartyAddress = async (address: string, selectedAddress: string) => {
  try {
    if (!address) return []
    const { suggestions } = await getSmartyAddressByStreet({
      search: address,
      selected: selectedAddress,
      language: 'en' // Request English language results
    })

    return suggestions
      .filter(item => item.secondary !== 'Apt')
      .filter(item => isEnglishText(item.street_line) && isEnglishText(item.city) && isEnglishText(item.state))
      .map(item => ({
        ...item,
        address: buildAddress(item)
      }))
  } catch (e: unknown) {
    logErrorOfApi(e, SMARTY_API_SERVICE_NAME)
    const { predictions } = await getPlaces(address)
    return predictions
      .filter(prediction => isEnglishText(prediction.description))
      .map(prediction => ({
        address: prediction.description,
        ...prediction
      }))
  }
}

export const getGeocodeByAddress = async (address: string) => {
  const geocoder = new google.maps.Geocoder()
  return await geocoder.geocode({
    address,
    componentRestrictions: {
      country: 'us'
    },
    language: 'en' // Force English language results
  })
}

type getEstimatesData = {
  start_address: string;
  start_lat: number | null
  start_lon: number | null;
  end_address: string;
  end_lat: number | null;
  end_lon: number | null;
  pickup_at?: string;
}

export type IEstimateRoad = {
  distance: number;
  duration: number;
}

export type IStats = {
  distance: number;
  duration: number;
}

export const getEstimateRoad = (data: getEstimatesData) => ApiService.get<getEstimatesData, Promise<IEstimateRoad>>('/estimate/road', {
  data
})

type createRideData = {
  phone_number: string;
  mta_client_id: string;
  mta_ref_id: string;
  first_name: string;
  last_name: string;
  start_address: string;
  start_lat: number | null
  start_lon: number | null;
  end_address: string;
  end_lat: number | null;
  end_lon: number | null;
  clients: number;
  companions: number;
  pcas: number;
  pickup_note: string;
  is_service_animal: boolean;
  pickup_at_dates: string;
  pickup_at_time: string;
  fare_type: string;
  equipment_codes: string[];
  ride_type_id: number;
  payment_type: string;
}
export const createRide = (data: createRideData) => ApiService.post<createRideData, Promise<any>>('/rides', {
  data
})

export type ITableRide = {
  id: number;
  mta_ref_id: string;
  first_name: string;
  last_name: string;
  pickup_at: string;
  dropoff_at: string;
  driver_first_name: string | null;
  driver_last_name: string | null;
  ride_type_id: number;
  destination_address: string;
  start_address: string;
  status: string;
  eta: number | null;
  payment_type: string;
}

export type IRidesData = {
  rides:{
    data: ITableRide[];
  }
}

export const getRides = (data: ISearchOptions) => ApiService.get<ISearchOptions, Promise<IRidesData>>('/rides', {
  data
})

export type IRide = {
  create_at: string;
  create_by: string;
  destination_address: string;
  destination_timezone: string;
  distance: number;
  dropoff_at: string;
  duration: number;
  equipment_codes: string[];
  fare_type: string;
  first_name: string;
  id: number;
  is_service_animal: number;
  last_name: string;
  mta_client_id: string;
  mta_ref_id: string;
  phone_number: string | null;
  pickup_at: string;
  pickup_note: string;
  pickup_timezone: string;
  ride_type: string;
  ride_type_display_name: string;
  ride_type_id: number;
  ride_type_image: string;
  ride_type_short_description: string;
  ride_type_timezone: string;
  seats: number;
  start_address: string;
  status: string;
  user_id: number;
  driver_first_name: string | null;
  driver_last_name: string | null;
  driver_phone_number: string | null;
  car_title: string | null;
  car_plate_number: string | null;
  eta: number | null;
  canceled_by: string | null;
  canceled_at: string | null;
  cancellation_reason: string | null;
  notes: string[];
  driver_id: number | null;
  start_lat: number;
  start_lon: number;
  destination_lat: number;
  destination_lon: number;
  tracking_url: string | null;
  payment_type: string;
}
export const retrieveRide = (id: number) => ApiService.get<undefined, Promise<{ ride: IRide }>>(`/rides/${id}`, {})

type cancelRideData = {
  id: number;
  reason: string;
}

export const cancelRide = (data: cancelRideData) => ApiService.post<{reason: string}, Promise<any>>(`/rides/${data.id}/company-cancel`, {
  data: {
    reason: data.reason
  }
})
